﻿import { useEffect } from 'react';

import { TreeView, TreeViewExpandChangeEvent, TreeViewItemClickEvent } from '@progress/kendo-react-treeview';
import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { Stage } from 'pages/courses/resources/components/Stage/Stage';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { helpContent } from 'pages/courses/resources/help';
import { FormattedMessage, useIntl } from 'react-intl';
import { PageHeader } from 'components/layout/page';
import { DefaultLayout } from 'components/layout';
import { Api } from 'pages/courses/resources/Api';
import type { Category, Resource } from 'types';
import { KendoIntlProvider } from 'app/culture';
import { Col, Row } from 'react-bootstrap';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

import 'pages/courses/resources/styles/main.scss';
import 'styles/global-styles.scss';
import 'styles/kendo-default-theme-with-overrides.scss';

function useDebounce<T>(value: T, delay: number): T {

    const [state, setState] = useSuperState({ debouncedValue: value });

    useEffect(() => {

        const timer = setTimeout(() => setState({ debouncedValue: value }), delay);

        return () => clearTimeout(timer);

    }, [value, delay]);

    return state.debouncedValue;

}

export const Resources = () => {

    const [state, setState] = useSuperState({
        activeId: null as string | null,
        activeResource: null as Resource,
        originalCategory: [] as any[],
        searchTerm: '',
        updatedCategory: [] as any[]
    });
    const { activeId, activeResource, originalCategory, searchTerm, updatedCategory } = state;

    const { languages, userId } = useApp();
    const { formatMessage, locale } = useIntl();
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    const handleSearchChange = (searchText: string) => {

        setState({ searchTerm: searchText });

    };

    const filterTree = (items: any[], searchTerm: string): any[] => items
        .map(item => {

            const hasMatchingText = item.text.toLowerCase().includes(searchTerm.trim().toLowerCase());
            const filteredChildren = item.items ? filterTree(item.items, searchTerm.trim()) : [];

            if (hasMatchingText || filteredChildren.length > 0) {

                return {
                    ...item,
                    expanded: filteredChildren.length > 0 || hasMatchingText,
                    items: filteredChildren.length > 0 ? filteredChildren : item.items
                };

            }

            return null;

        })
        .filter(Boolean);

    const buildTreeData = (categories: Category[]) => categories.map(category => {

        const products = category.Products?.map(product => {

            const courses = product.Courses?.map(course => {

                const resources = course.Resources?.map(resource => buildResourceNode(
                    resource,
                    `category-${category.Id}-${category.Name}-product-${product.Id}-${product.Name}-course-${course.Id}-${course.Name}`,
                    false
                )).filter(Boolean);

                return resources.length > 0 ? {
                    ...course,
                    id: `category-${category.Id}-${category.Name}-product-${product.Id}-${product.Name}-course-${course.Id}-${course.Name}`,
                    expanded: false,
                    items: resources,
                    text: course.Name
                } : null;

            }).filter(Boolean);

            return courses && courses.length > 0 ? {
                ...product,
                id: `category-${category.Id}-${category.Name}-product-${product.Id}-${product.Name}`,
                expanded: false,
                items: courses,
                text: product.Name
            } : null;

        }).filter(Boolean);

        return products.length > 0 ? {
            ...category,
            id: `category-${category.Id}-${category.Name}`,
            expanded: false,
            items: products,
            text: category.Name
        } : null;

    }).filter(Boolean);

    const buildResourceNode = (resource: Resource, parentPath: string, isLanguageNode = false): any => {

        const currentPath = `${parentPath}-resource-${resource.Id}-${resource.Name}`;

        const languageMatch = isLanguageNode && resource.Id === 0
            ? languages.find(l => l.Id.toString() === resource.Name)
            : null;

        return {
            ...resource,
            id: currentPath,
            expanded: false,
            items: resource.ChildResources?.map(child => buildResourceNode(child, currentPath, resource.Name === 'Student Resources')
            ) || [],
            text: languageMatch ? languageMatch.Name : resource.Name
        };

    };
    const toggleExpanded = (items: any[], targetId: string): any[] => items.map(item => {

        if (item.id === targetId) {

            return { ...item, expanded: !item.expanded };

        }
        if (item.items) {

            return { ...item, items: toggleExpanded(item.items, targetId) };

        }
        return item;

    });

    const onExpandChange = (event: TreeViewExpandChangeEvent) => {

        const updatedData = toggleExpanded(updatedCategory, event.item.id);
        setState({ updatedCategory: updatedData });

    };

    const onItemClick = (event: TreeViewItemClickEvent) => {

        const { item } = event;

        if (item.items && item.items.length > 0) {

            const updatedData = toggleExpanded(updatedCategory, item.id);
            setState({ updatedCategory: updatedData });

        } else {

            setState({ activeId: item.id, activeResource: item });

        }

    };

    const itemRender = (li: any) => {

        const dataItem = li.item;
        const isActive = dataItem?.id === activeId;
        return (
            <div
                style={{
                    backgroundColor: isActive ? 'rgb(231 231 231)' : 'transparent',
                    cursor: 'pointer',
                    fontWeight: isActive ? 'bold' : 'normal',
                    padding: '8px 8px',
                    textWrap: 'wrap'
                }}
                className="d-flex align-items-center justify-content-between w-100"
                title={dataItem.text}
            >
                <div className="d-flex align-items-center">
                    {iconToShow(dataItem)}
                    {dataItem.text}
                </div>
                {dataItem.Url && <i className={`ms-3 fa-solid ${dataItem.Url.includes('zip') ? 'fa-download text-primary' : dataItem.Url.includes('pdf') ? 'fa-file-pdf text-danger' : 'fa-file'} fw-bold fa-lg`}></i>}
            </div>
        );

    };

    const iconToShow = (dataItem: any) => {

        if (dataItem.IconURL) {

            return (
                <img
                    className="p-1 me-3"
                    src={dataItem.IconURL}
                    style={{ backgroundColor: dataItem.IconURL.includes('svg') ? `#${dataItem.Color ? dataItem.Color.split(';')[0] : ''}` : 'transparent' }}
                    width={30}
                />
            );

        }

    };

    useEffect(() => {

        if (debouncedSearchTerm.trim() === '') {

            setState({ updatedCategory: originalCategory });

        } else {

            const filtered = filterTree(originalCategory, debouncedSearchTerm);
            setState({ updatedCategory: filtered });

        }

    }, [debouncedSearchTerm, originalCategory]);

    useEffect(() => {

        const loadCategoriesWithProducts = async () => {

            try {

                const value = await Api.GetCategoriesWithProducts(userId);

                // Gather every product id across every category, in one flat list
                const allProductIds = value.flatMap((category: any) => category.Products.map((product: any) => product.Id)
                );

                if (allProductIds.length === 0) {

                    setState({ originalCategory: buildTreeData(value), updatedCategory: buildTreeData(value) });
                    return;

                }

                try {

                    // Single call instead of one-per-category
                    const newProducts = await Api.GetProductsWithResources(userId, allProductIds.toString());

                    // Redistribute the flat product list back onto each category by ProductId
                    const productsById = new Map(newProducts.map((p: any) => [p.Id, p]));

                    const updatedCategoryList = value.map((category: any) => ({
                        ...category,
                        Products: category.Products
                            .map((product: any) => productsById.get(product.Id))
                            .filter(Boolean)
                    }));

                    setState({ originalCategory: buildTreeData(updatedCategoryList), updatedCategory: buildTreeData(updatedCategoryList) });

                } catch (error) {

                    console.error(error);

                }

            } catch {

                // Failed to load categories

            }

        };
        loadCategoriesWithProducts();

    }, []);

    return (
        <DefaultLayout pageTitle={formatMessage({ id: 'course_resources.96e8f82d', defaultMessage: 'Course Resources' })}>
            <KendoIntlProvider>
                <LocalizationProvider language={locale}>
                    <IntlProvider locale={locale}>
                        <div >
                            <PageHeader helpContent={helpContent} title={formatMessage({ id: 'instructor_resources.ad9a5c84', defaultMessage: 'Instructor Resources' })} />

                            <Row className="resources m-0">
                                <Col lg={4} md={12} xs={12}>
                                    <GMetrixInput
                                        onChange={e => handleSearchChange(e.target.value)}
                                        placeholder={formatMessage({ id: 'search.0ab06b2c', defaultMessage: 'Search' })}
                                        value={searchTerm}
                                    />
                                    <div className="mt-4 card card-body">
                                        {updatedCategory.length > 0 ? (
                                            <TreeView
                                                data={updatedCategory}
                                                expandIcons={true}
                                                item={itemRender}
                                                onExpandChange={onExpandChange}
                                                onItemClick={onItemClick}
                                            />
                                        ) : originalCategory.length > 0 ? (
                                            <div
                                                className="text-center py-4"
                                                style={{ fontSize: '20px ' }}
                                            >

                                                <FormattedMessage defaultMessage="No Results Found" id="no_results_found.f1d71249" />
                                            </div>
                                        )
                                            : (
                                                <div className="text-center py-4">
                                                    <GMetrixLoader size="large" />
                                                </div>
                                            )}
                                    </div>
                                </Col>
                                <Col className="mt-4 mt-lg-0" lg={8} xs={12}>
                                    <Stage resource={activeResource} />
                                </Col>
                            </Row>

                        </div>

                    </IntlProvider>
                </LocalizationProvider>
            </KendoIntlProvider>
        </DefaultLayout>
    );

};